import { useMutation, useQueryClient } from 'react-query';
import { errorMock } from '@vfit/consumer/data-access';
import { API } from '@vfit/shared/data-access';
import { nextClient, LoggerInstance, INextError, CustomOptions } from '@vfit/shared/data-access';
import { IInputs, IModifyValidAddress, IValidateAddressResponse } from '@vfit/shared/models';
import { datadogRum } from '@datadog/browser-rum';

/**
 * This method is used to validate the address from the Google Autocomplete
 * @param payload
 * @param customOptions
 * @returns
 */
const validateAddressService = (
  payload: IInputs,
  customOptions?: CustomOptions
): Promise<IValidateAddressResponse> =>
  nextClient.post(API.VALIDATE_ADDRESS, payload, {
    ...(customOptions?.headers && { headers: customOptions.headers }),
    ...(customOptions?.silentLoginHeaders && {
      silentLoginHeaders: customOptions.silentLoginHeaders,
    }),
  });

export const useValidateAddressMutation = () => {
  const queryClient = useQueryClient();

  return useMutation('setAddress', (placeObject: any) =>
    queryClient.setQueryData('coverageToolInputAddress', placeObject)
  );
};

export const useValidateAddress = (customOptions?: CustomOptions) => {
  const queryClient = useQueryClient();
  let keyString = 'validatedAddress';
  return useMutation(
    'addressValidation',
    (req: IModifyValidAddress) => {
      const {
        street,
        streetNumber,
        city,
        stateOrProvince,
        postalCode,
        displayStateOrProvince,
        latitude,
        longitude,
      } = req.placeObject;
      const newFormattedData = {
        street,
        streetNumber,
        city,
        stateOrProvince,
        postalCode,
        displayStateOrProvince,
        latitude,
        longitude,
      };
      const payload: IInputs = {
        ...newFormattedData,
        displayStateOrProvince: req?.placeObject.stateOrProvince,
      };
      return validateAddressService(payload, customOptions);
    },
    {
      onMutate: async (data) => {
        if (data?.isBilling === true) {
          keyString = 'billingAddress';
        } else if (data?.isDelivery === true) {
          keyString = 'deliveryAddress';
        } else if (data?.isPickup === true) {
          keyString = 'pickupAddress';
        }
      },
      onSuccess: (data: IValidateAddressResponse, variables) => {
        const validErrorCode = ['0', '1'];
        if (data?.validAddressList && validErrorCode.includes(data?.errorCode)) {
          localStorage.setItem(keyString, JSON.stringify(data));
          queryClient.setQueryData(keyString, data);
        } else {
          datadogRum.addError({
            inputVariables: variables?.placeObject,
            address: data?.validAddressList?.[0] || 'NO_ADDRESS_LIST',
            error_code: data?.errorCode || 'NO_ERROR_CODE',
          });
          queryClient.setQueryData(
            keyString,
            errorMock(
              'validateAddress',
              {
                status: '200',
                statusText: `ERROR on ${keyString}`,
                url: API.VALIDATE_ADDRESS,
              },
              `L'indirizzo inserito non è valido (Code: ${data.errorCode})`
            )
          );
          localStorage.setItem(
            keyString,
            JSON.stringify(
              errorMock(
                'addressValidation',
                {
                  status: '200',
                  statusText: `ERROR on ${keyString}`,
                  url: API.VALIDATE_ADDRESS,
                },
                `L'indirizzo inserito non è valido (Code: ${data.errorCode})`
              )
            )
          );
        }
        customOptions?.onSuccess?.(data);
      },
      onError: (error: INextError) => {
        LoggerInstance.error(`ERROR - ${keyString}: `, error);
        queryClient.setQueryData(keyString, errorMock(`${keyString}`, error));
        localStorage.setItem(`${keyString}`, JSON.stringify(errorMock(`${keyString}`, error)));
        customOptions?.onError?.(error);
      },
    }
  );
};
